import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import jwtInterceoptor from "../../components/shared/jwtInterceptor";
import moment from "moment";
import Swal from "sweetalert2";
import { LinearProgress, Modal, Box } from "@mui/material"; // Import Modal and Box from Material-UI
import { toast, ToastContainer } from "react-toastify";
import OrgContext from "../../organisation/OrgContext";
import { useNavigate } from "react-router-dom";
import { frFR, enUS } from "@mui/x-data-grid";
import useAuthHelper from "../../helpers/AuthHelper";
import { baseURL } from "../../services/api";

const FacturesRecurrentes = () => {
  const [t, i18n] = useTranslation();
  const { organisation } = useContext(OrgContext);
  const [invoices, setInvoices] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();
  const { onlyCommercial, hadDeleteAccess, hadEditAccess } = useAuthHelper();
  const [selectedInvoice, setSelectedInvoice] = useState(null); // State for selected invoice
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility

  useEffect(() => {
    onlyCommercial();
    if (organisation.id) {
      fetchInvoices();
    }
  }, []);

  const fetchInvoices = async () => {
    setLoadingData(true);
    const response = await jwtInterceoptor.get(
      `/data/recurring-invoices/${organisation.id}`
    );
    if (response.status === 200) {
      setInvoices(response.data);
    }
    setLoadingData(false);
  };

  const deleteItem = async (inv) => {
    Swal.fire({
      title: t("Are you sure?"),
      text: t("You won't be able to revert this!"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: t("Yes, delete it!"),
      cancelButtonText: t("Cancel"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await jwtInterceoptor.delete(
            `/data/recurring-invoices/${inv.id}`
          );
          if (response.status === 200) {
            setInvoices(invoices.filter((el) => el.id !== inv.id));
            Swal.fire(
              t("Deleted!"),
              t("Your record has been deleted."),
              "success"
            );
          } else {
            Swal.fire({
              icon: "error",
              title: t("Error"),
              text: t("Failed to delete the recurring invoice."),
            });
          }
        } catch (error) {
          console.error("Error deleting recurring invoice:", error);
          Swal.fire({
            icon: "error",
            title: t("Error"),
            text: t("Something went wrong! Please try again."),
          });
        }
      }
    });
  };
  const [invoiceProductsState, setInvoiceProductsState] = useState([]);

  useEffect(() => {
    if (!selectedInvoice) return; // If no invoice is selected, do nothing

    const fetchInvoiceDetails = async () => {
      try {
        const response = await jwtInterceoptor.get(
          `${baseURL}/data/recurring-invoice/${selectedInvoice.id}`
        );
        const inv = response.data.data;
        console.log("invoiceData", inv);

        if (selectedInvoice.type === "depense") {
          setInvoiceProductsState(
            inv.accounts.map((product) => ({
              account_id: product.account_id,
              name: product.account?.name || "N/A",
              tva_id: product.tva_id,
              tva_rate: product.tva.rate,
              tva: product.tva.rate,
              tvaa: product.tva,
              totalHt: product.total_ht,
              totalTtc: product.total_ttc,
              details: product.designation,
              qty: 1, // Default quantity for expenses
              unitPrice: product.total_ht,
            }))
          );
        } else {
          setInvoiceProductsState(
            inv.products.map((product) => ({
              product_id: product.product_id,
              name: product.product?.name || "N/A",
              qty: product.qty,
              unitPrice: product.product?.sellingPrice || 0,
              tva: product.tva_rate,
              totalHt: product.total_ht,
              totalTtc: product.total_ttc,
              details: product.details,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching invoice products:", error);
        toast.error(t("Something went wrong! Please try again."));
      }
    };

    fetchInvoiceDetails();
  }, [selectedInvoice]);
  const handleViewDetails = (invoice) => {
    // Open the modal and set the selected invoice
    setSelectedInvoice(invoice); // Set the selected invoice
    console.log("invoice", invoice);
    setIsModalOpen(true); // Open the modal
    //fonction fetch invoice product or accounts
  };

  const handleCloseModal = () => {
    setIsModalOpen(false); // Close the modal
    setSelectedInvoice(null); // Clear the selected invoice
  };

  // Function to render the read-only form inside the modal
  const renderReadOnlyForm = () => {
    if (!selectedInvoice) return null;

    return (
      <div style={{ maxHeight: "900px", overflowY: "auto" }}>
        <h2>{t("Facture détails")}</h2>
        <div className="form-group">
          <label>{t("Type de Facture")}</label>
          <input
            type="text"
            className="form-control"
            value={selectedInvoice.type}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Fournisseur/Client")}</label>
          <input
            type="text"
            className="form-control"
            value={selectedInvoice.client?.companyName || ""}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Titre")}</label>
          <input
            type="text"
            className="form-control"
            value={selectedInvoice.title}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Fréquence")}</label>
          <input
            type="text"
            className="form-control"
            value={t(selectedInvoice.frequency)}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Date de début")}</label>
          <input
            type="text"
            className="form-control"
            value={moment(selectedInvoice.start_date).format("YYYY-MM-DD")}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Date de fin")}</label>
          <input
            type="text"
            className="form-control"
            value={selectedInvoice.due_date ? selectedInvoice.due_date : ""}
            readOnly
          />
        </div>
        <div className="form-group">
          <label>{t("Statut")}</label>
          <input
            type="text"
            className="form-control"
            value={selectedInvoice.is_active ? t("Active") : t("Inactive")}
            readOnly
          />
        </div>
        <h4>{t("Produits/Services")}</h4>
        <table className="table">
          <thead>
            <tr>
              <th>{t("Détails")}</th>
              <th>{t("Quantité")}</th>
              <th>{t("Prix Unitaire HT")}</th>
              <th>{t("TVA %")}</th>
              <th>{t("Total HT")}</th>
              <th>{t("Total TTC")}</th>
            </tr>
          </thead>
          <tbody>
            {invoiceProductsState?.map((product, index) => (
              <tr key={index}>
                <td>{`${product.name}-${product?.details}` || "N/A"}</td>
                <td>{Number(product.qty).toFixed(2)}</td>
                <td>
                  {Number(
                    parseFloat(product.totalHt) / parseFloat(product.qty)
                  ).toFixed(2)}
                </td>
                <td>{Number(product.totalTtc - product.totalHt).toFixed(2)}</td>
                <td>{Number(parseFloat(product.totalHt))}</td>
                <td>{Number(product.totalTtc).toFixed(2)}</td>
              </tr>
            )) || (
              <tr>
                <td colSpan="6" className="text-center">
                  {t("No products/services available.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="invoiceSummary">
          <table>
            <tr>
              <td>{t("Total HT")}:</td>
              <td>
                {Number(selectedInvoice.total_ht).toFixed(2)}{" "}
                {organisation?.currency?.code}
              </td>
            </tr>
            <tr>
              <td>{t("Total TVA")}:</td>
              <td>
                {Number(selectedInvoice.total_tva).toFixed(2)}{" "}
                {organisation?.currency?.code}
              </td>
            </tr>
            <tr>
              <td>{t("Total TTC")}:</td>
              <td>
                {Number(selectedInvoice.total_ttc).toFixed(2)}{" "}
                {organisation?.currency?.code}
              </td>
            </tr>
          </table>
        </div>
        <button className="btn btn-secondary" onClick={handleCloseModal}>
          {t("Close")}
        </button>
      </div>
    );
  };

  const columns = [
    {
      field: "ID",
      headerName: t("ID"),
      valueGetter: (params) => `${params.row?.id}`,
      width: 100,
    },
    {
      flex: 1,
      field: "Fournisseur/Client",
      headerName: t("Fournisseur/Client"),
      valueGetter: (params) => params.row.client.companyName || " ",
    },
    {
      flex: 1,
      field: "Type de facture",
      headerName: t("Type de facture"),
      valueGetter: (params) => `Facture ${params.row.type}` || " ",
    },
    {
      flex: 1,
      field: "Status de facture",
      headerName: t("Status de facture"),
      valueGetter: (params) =>
        params.row.is_active ? t("Active") : t("Inactive"),
    },
    {
      flex: 1,
      field: "Date de Création",
      headerName: t("Date de Création"),
      valueGetter: (params) =>
        `${moment(params.row.created_at).format("DD/MM/YYYY") || ""}`,
    },

    {
      flex: 1,
      field: "fréquence",
      headerName: t("Fréquence"),
      valueGetter: (params) => `${t(params.row.frequency)}` || "",
    },
    {
      flex: 1,
      field: "Total TTC",
      headerName: t("Total TTC"),
      valueGetter: (params) =>
        `${
          `${Number(params.row.total_ttc).toFixed(2)} ` +
            `${organisation?.currency?.code}` || ""
        }`,
    },
    {
      field: "action",
      headerName: t("ACTIONS"),
      width: 250,
      sortable: false,
      disableClickEventBubbling: true,
      renderCell: (params) => {
        const onDelete = () => {
          if (!hadDeleteAccess()) {
            toast.error(
              t("Vous n'avez pas les droits pour supprimer cette facture."),
              {
                position: "top-right",
                autoClose: 5000,
              }
            );
            return;
          }
          deleteItem(params.row);
        };
        const onEdit = () => {
          if (!hadEditAccess()) {
            toast.error(
              t("Vous n'avez pas les droits pour supprimer cette facture"),
              {
                position: "top-right",
                autoClose: 5000,
              }
            );
            return;
          }
          navigate(`/facturerecurrente/edit/${params.row.id}`);
        };
        const onDetails = () => handleViewDetails(params.row); // Updated handler

        return (
          <div className="btn-list">
            <button
              onClick={onEdit}
              type="button"
              className="btn btn-sm btn-primary"
            >
              <span className="fe fe-edit"> </span>
            </button>
            <button
              onClick={onDetails}
              type="button"
              className="btn btn-sm btn-secondary"
            >
              <span className="fe fe-eye"> </span>
            </button>
            <button
              onClick={onDelete}
              type="button"
              className="btn btn-sm btn-danger"
            >
              <span className="fe fe-trash-2"> </span>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <ToastContainer />
      {/* Modal for viewing invoice details */}
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          {renderReadOnlyForm()}
        </Box>
      </Modal>
      <div className="row" style={{ zIndex: 1 }}>
        <div className="col-lg-12">
          <div className="card">
            <div className="card-header container-fluid row">
              <div className="col-md-9 col-sm-12 card-title">
                {t("Factures récurrentes")}
              </div>
              <div className="col-md-3 card-title">
                <button
                  className="btn btn-primary"
                  onClick={() => navigate("/facturerecurrente/add")}
                >
                  {t("Ajouter une facture récurrente")}
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div style={{ height: 600, width: "100%" }}>
                  <DataGrid
                    rows={invoices}
                    columns={columns}
                    pagination={false}
                    localeText={
                      i18n.language === "fr"
                        ? frFR.components.MuiDataGrid.defaultProps.localeText
                        : enUS.components.MuiDataGrid.defaultProps.localeText
                    }
                    slots={{
                      loadingOverlay: LinearProgress,
                      toolbar: GridToolbar,
                    }}
                    loading={loadingData}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FacturesRecurrentes;
